/**
 * Variant & Global Media Architecture
 * Handles variant:change events to fetch and replace the product gallery
 * using the Section Rendering API.
 *
 * Autoplay: the theme's native mechanism (carousel:settle → onMediaSettle_fn)
 * only works in carousel mode where scrollend fires on the scroll-carousel
 * element. In 2-column grid mode the page scrolls, not the carousel, so that
 * chain never fires. We fill this gap with an IntersectionObserver that calls
 * videoMedia.play() directly when a video cell enters the viewport — the same
 * call the theme ultimately makes, just without the event indirection.
 *
 * Runs on both the initial page render (DOMContentLoaded) and after every
 * variant swap, so the first load is covered without needing a variant change.
 */

/**
 * Wire up autoplay for all video cells in a gallery element.
 * Safe to call on both the initial DOM and after a variant-swap DOM replacement.
 *
 * Calls play() directly on each video-media element as it enters the viewport,
 * using IntersectionObserver — a native browser API. No synthetic events are
 * dispatched; this is the most direct and organic path to playback.
 */
function initGalleryAutoplay(gallery) {
  if (!gallery.hasAttribute('autoplay-media')) return;

  const videoCells = Array.from(
    gallery.querySelectorAll('[data-media-type="video"], [data-media-type="external_video"]')
  );
  if (!videoCells.length) return;

  function playVideoInCell(cell) {
    // Set preload="metadata" so the browser has buffered data before play() fires,
    // mirroring VideoMedia.connectedCallback's own inView handler.
    cell.querySelectorAll('video[preload="none"]').forEach((vid) => {
      vid.setAttribute('preload', 'metadata');
    });
    const videoMedia = cell.querySelector('video-media');
    if (videoMedia) videoMedia.play();
  }

  // Use IntersectionObserver to call play() as each video cell enters the viewport.
  // Works for both carousel mode (cell snaps into view) and grid mode (page scroll).
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        playVideoInCell(entry.target);
        // Unobserve after first trigger so scroll-back doesn't re-fire.
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.5 }
  );

  videoCells.forEach((cell) => observer.observe(cell));
}

// ── Externally-paused video recovery ─────────────────────────────────────────
// When the theme's group mechanic pauses a video (because another video in the
// same group started playing), the video-media element lands in a state where:
//  - [loaded] is present (it was already playing), so the theme's CSS hides the
//    play button: video-media[loaded]::part(play-button) { opacity: 0 }
//  - The theme's { once: true } click→play handler has already been consumed
// This leaves the user with no way to restart the video. We fix it for any
// video-media inside a product-gallery — whether autoplay is on or off —
// by revealing the play button and re-registering the click handler.
// The [loaded] check scopes this to videos that have actually been played,
// not ones in their initial pre-interaction state.
document.addEventListener('media:pause', (event) => {
  const vm = event.target;
  if (!(vm instanceof HTMLElement) || vm.tagName.toLowerCase() !== 'video-media') return;
  if (!vm.closest('product-gallery')) return;
  if (!vm.hasAttribute('loaded')) return; // not yet played — nothing to restore

  vm.setAttribute('paused-autoplay', '');

  // Re-register click→play since the theme's { once: true } handler is consumed.
  vm.addEventListener('click', () => {
    vm.removeAttribute('paused-autoplay');
    vm.play();
  }, { once: true, signal: vm._abortController?.signal });
});

document.addEventListener('media:play', (event) => {
  event.target.removeAttribute?.('paused-autoplay');
});

// ── Thumbnail selection race-condition fix ────────────────────────────────────
// ProductGallery.onVariantChange_fn (theme.js) scrolls the gallery to the new
// variant's media via scrollIntoView({behavior: 'smooth'}), but never sets the
// `_hasProgrammaticScroll` flag that ProductGalleryNavigation's
// IntersectionObserver checks before reacting to scroll-driven visibility
// changes (that flag is only set from ProductGalleryNavigation's own
// onButtonClicked, i.e. a manual thumbnail click). During the smooth scroll,
// intermediate cells cross the observer's 50% threshold and it "settles" the
// aria-current highlight on an intermediate thumbnail instead of the actual
// destination — the gallery image ends up correct, but the wrong thumbnail is
// marked selected.
//
// We try to set aria-current to the variant's real featured media right away
// (no flash, for the common case where the nav is already in the DOM — i.e.
// every non-custom-gallery product), then keep watching for ~1s in case the
// nav isn't there yet (custom-gallery products still fetching/swapping the
// whole section) or the buggy observer clobbers our correction once the
// scroll animation gets going. Either way, the fix applies the instant it's
// possible to apply it, instead of waiting out a fixed delay regardless.
// Tracks the in-flight correction observer per section, so rapid variant
// switching doesn't leave multiple overlapping observers alive at once.
const thumbnailFixObservers = new WeakMap();

document.addEventListener('variant:change', (event) => {
  const featuredMediaId = event.detail.variant?.featured_media?.id;
  if (!featuredMediaId) return;

  const section = event.target.closest('.shopify-section');
  if (!section) return;

  thumbnailFixObservers.get(section)?.disconnect();

  const applyCorrectSelection = () => {
    const nav = section.querySelector('product-gallery-navigation');
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('button[data-media-id]'));
    const target = buttons.find((button) => button.dataset.mediaId === String(featuredMediaId));
    if (!target || target.getAttribute('aria-current') === 'true') return;

    buttons.forEach((button) => {
      button.setAttribute('aria-current', button === target ? 'true' : 'false');
    });
  };

  applyCorrectSelection();

  // Catches both: the buggy observer clobbering our correction while the
  // scroll animation settles, and the nav/gallery being replaced wholesale
  // (custom-gallery section re-render) after our initial attempt missed it.
  const observer = new MutationObserver(applyCorrectSelection);
  observer.observe(section, {
    attributes: true,
    attributeFilter: ['aria-current'],
    subtree: true,
    childList: true,
  });
  thumbnailFixObservers.set(section, observer);

  setTimeout(() => observer.disconnect(), 1000);
});

// ── Initial page load ─────────────────────────────────────────────────────────
// Wire up every custom gallery already in the DOM (main product, featured
// product, etc.) before any variant change has occurred.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('product-gallery[data-custom-gallery]').forEach((gallery) => {
    setTimeout(() => initGalleryAutoplay(gallery), 150);
  });
});

// Cache keyed by `${sectionId}:${variantId}` so galleries from different
// sections (main product vs featured product) never share cached HTML.
const galleryCache = new Map();

document.addEventListener('variant:change', async (event) => {
  const variant = event.detail.variant;
  if (!variant) return;

  // event.target is the <form> element. We never read form.id because
  // HTMLFormElement's named-property lookup makes `form.id` return the
  // <input name="id"> element inside the form instead of the ID attribute
  // string. Traversing up to the nearest .shopify-section and then querying
  // down is immune to this issue and correctly scopes to the right gallery
  // when both a main-product and a featured-product section are on the page.
  const form = event.target;
  const section = form.closest('.shopify-section');
  if (!section) return; // quick-buy is inside a <dialog>, not a section — skip

  const gallery = section.querySelector('product-gallery[data-custom-gallery]');
  if (!gallery) return;

  const sectionId = section.id.replace('shopify-section-', '') || 'main-product';

  // Cache the current gallery state before swapping.
  // Strip `playing` attributes from the clone so that when it is restored the
  // video-media elements are in a clean stopped state and play() won't be
  // skipped by BaseMedia's `if (!this.playing)` guard.
  const previousVariantId = event.detail.previousVariant?.id;
  const previousCacheKey = previousVariantId ? `${sectionId}:${previousVariantId}` : null;
  if (previousCacheKey && !galleryCache.has(previousCacheKey)) {
    const liveClone = gallery.cloneNode(true);
    liveClone.querySelectorAll('[playing]').forEach((el) => el.removeAttribute('playing'));
    galleryCache.set(previousCacheKey, liveClone);
  }

  const cacheKey = `${sectionId}:${variant.id}`;

  // CLS Protection: Lock height
  const currentHeight = gallery.getBoundingClientRect().height;
  gallery.style.height = `${currentHeight}px`;

  // Hide until the correct media lands. theme.js's ProductGallery still
  // reacts to this same variant:change on the old (soon-to-be-replaced)
  // gallery instance — its featured-media index is only meaningful against
  // the full product media library, not this filtered per-variant cell
  // list, so it can jump the old gallery to an unrelated cell (reported as
  // "the third image" flashing) before this swap replaces it moments later.
  // Hiding here (synchronously, before that mutation can paint) suppresses
  // it without touching theme.js. Scoped via the [data-custom-gallery]
  // selector above, so this only ever applies to this custom gallery.
  section.setAttribute('data-gallery-transitioning', '');

  try {
    let newGallery;

    if (galleryCache.has(cacheKey)) {
      newGallery = galleryCache.get(cacheKey).cloneNode(true);
    } else {
      // Fetch new gallery HTML.
      // Use the gallery's own product URL (stored in data-product-url) rather
      // than window.location.pathname so that a featured-product section
      // showing a different product fetches the correct section HTML.
      const productPath = gallery.dataset.productUrl || window.location.pathname;
      const url = new URL(productPath, window.location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Failed to fetch section: ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      newGallery = doc.querySelector('product-gallery');

      if (newGallery) {
        // Preload visible images to prevent flash
        const imagesToLoad = Array.from(newGallery.querySelectorAll('img')).slice(0, 2); // Preload first 2 images
        await Promise.all(imagesToLoad.map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            const image = new Image();
            image.src = img.src;
            image.srcset = img.srcset;
            image.sizes = img.sizes;
            image.onload = resolve;
            image.onerror = resolve; // Continue even if one fails
          });
        }));
      }
    }

    if (newGallery) {
      // Apply the locked height to the NEW gallery before swapping
      // to ensure no layout shift or collapse during the swap.
      newGallery.style.height = `${currentHeight}px`;

      // Swap the DOM
      gallery.replaceWith(newGallery);

      // The correct media is in the DOM now — for a cache hit this is
      // already fully loaded (cloned from a gallery that was previously on
      // screen), and for a fresh fetch we've already awaited its images
      // above — so reveal on this frame instead of coupling it to the
      // autoplay-wiring delay below, which would otherwise hold every swap
      // hidden for ~150ms even when the image was ready immediately.
      section.removeAttribute('data-gallery-transitioning');

      // Populate the cache for next time now that the swap is done, so
      // cloning this (potentially large) gallery never sits between
      // fetch-complete and paint on the path the user is waiting on.
      if (!galleryCache.has(cacheKey)) {
        galleryCache.set(cacheKey, newGallery.cloneNode(true));
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          // Scope the post-swap lookup to the same section — `section` is
          // still in the DOM and now contains the newly-inserted gallery.
          const updatedGallery = section.querySelector('product-gallery');
          if (!updatedGallery) return;

          // Release the height lock
          updatedGallery.style.height = '';

          // Wire up autoplay for both carousel and grid layouts using the same
          // mechanism as the initial page load path.
          initGalleryAutoplay(updatedGallery);
        }, 150); // Give video-media custom elements time to run connectedCallback
      });
    } else {
      throw new Error('New gallery element not found in response');
    }
  } catch (error) {
    console.error('[Variant Gallery] Fetch error:', error);
    // Restore styles on failure so the page layout isn't broken
    gallery.style.height = '';
    section.removeAttribute('data-gallery-transitioning');
  }
});
